import { createCheckout } from '../checkout-controller';
import { parseZippyEnvironment } from '../config/environments';
import type {
  CheckoutPolicy,
  PaymentSession,
  ZippyEnvironment,
  ZippyPayCheckoutInstance,
  ZippyPayConfig,
  ZippyPayError,
  ZippyTheme,
} from '../types';

const ELEMENT_TAG = 'zippy-pay-checkout';

const THEME_ATTRIBUTE_MAP: Record<string, keyof ZippyTheme> = {
  'theme-primary': 'primary',
  'theme-primary-foreground': 'primaryForeground',
  'theme-background': 'background',
  'theme-surface': 'surface',
  'theme-text': 'text',
  'theme-text-muted': 'textMuted',
  'theme-border': 'border',
  'theme-error': 'error',
  'theme-success': 'success',
  'theme-border-radius': 'borderRadius',
  'theme-font-family': 'fontFamily',
  'theme-mode': 'mode',
};

export class ZippyPayCheckoutElement extends HTMLElement {
  private instance: ZippyPayCheckoutInstance | null = null;

  static get observedAttributes(): string[] {
    return [
      'mode',
      'session-id',
      'environment',
      'checkout-policy',
      'mobile-app-policy',
      ...Object.keys(THEME_ATTRIBUTE_MAP),
    ];
  }

  connectedCallback(): void {
    this.mount();
  }

  disconnectedCallback(): void {
    this.unmount();
  }

  attributeChangedCallback(): void {
    if (this.isConnected) {
      this.unmount();
      this.mount();
    }
  }

  set clientToken(value: string) {
    this.dataset.clientToken = value;
    if (this.isConnected) {
      this.unmount();
      this.mount();
    }
  }

  get clientToken(): string {
    return this.dataset.clientToken ?? this.getAttribute('client-token') ?? '';
  }

  private mount(): void {
    const config = this.readConfig();
    if (!config) {
      return;
    }

    this.innerHTML = '';
    const mode = config.mode ?? 'inline';
    let container: HTMLElement | undefined;

    if (mode === 'inline') {
      const host = document.createElement('div');
      this.appendChild(host);
      container = host;
    }

    this.instance = createCheckout({
      ...config,
      container,
      mode,
      onReady: (session: PaymentSession) => this.dispatch('zippy-ready', session),
      onSessionUpdate: (session: PaymentSession) =>
        this.dispatch('zippy-session-update', session),
      onComplete: (session: PaymentSession) => this.dispatch('zippy-complete', session),
      onExpired: (session: PaymentSession) => this.dispatch('zippy-expired', session),
      onError: (error: ZippyPayError) => this.dispatch('zippy-error', error),
      onClose: () => this.dispatch('zippy-close', null),
    });

    if (config.mode === 'modal') {
      this.instance.open();
    }
  }

  private unmount(): void {
    this.instance?.destroy();
    this.instance = null;
    this.innerHTML = '';
  }

  private readConfig(): ZippyPayConfig | null {
    const environmentAttr = this.getAttribute('environment') ?? 'production';
    const sessionId = this.getAttribute('session-id') ?? '';
    const clientToken = this.clientToken;
    const mode = (this.getAttribute('mode') as ZippyPayConfig['mode']) ?? 'inline';

    if (!sessionId || !clientToken) {
      return null;
    }

    let environment: ZippyEnvironment;
    try {
      environment = parseZippyEnvironment(environmentAttr);
    } catch {
      return null;
    }

    const theme: ZippyTheme = {};
    for (const [attr, key] of Object.entries(THEME_ATTRIBUTE_MAP)) {
      const value = this.getAttribute(attr);
      if (value) {
        if (key === 'mode') {
          theme.mode = value as ZippyTheme['mode'];
        } else {
          theme[key] = value;
        }
      }
    }

    return {
      environment,
      sessionId,
      clientToken,
      mode,
      theme,
      checkoutPolicy: parseCheckoutPolicyAttribute(this.getAttribute('checkout-policy')),
      mobileApp: parseMobileAppAttribute(this.getAttribute('mobile-app-policy')),
    };
  }

  private dispatch<T>(name: string, detail: T): void {
    this.dispatchEvent(
      new CustomEvent(name, {
        detail,
        bubbles: true,
        composed: true,
      }),
    );
  }

  open(): void {
    this.instance?.open();
  }

  close(): void {
    this.instance?.close();
  }

  destroy(): void {
    this.instance?.destroy();
    this.instance = null;
  }
}

function parseCheckoutPolicyAttribute(value: string | null): CheckoutPolicy | undefined {
  if (value === 'QR' || value === 'MANUAL') {
    return value;
  }
  return undefined;
}

function parseMobileAppAttribute(value: string | null): ZippyPayConfig['mobileApp'] {
  if (value === 'off' || value === 'option' || value === 'enforce') {
    return { policy: value };
  }
  return undefined;
}

export function defineZippyPayCheckoutElement(): void {
  if (typeof customElements === 'undefined') {
    return;
  }
  if (!customElements.get(ELEMENT_TAG)) {
    customElements.define(ELEMENT_TAG, ZippyPayCheckoutElement);
  }
}

export { ELEMENT_TAG };

declare global {
  interface HTMLElementTagNameMap {
    'zippy-pay-checkout': ZippyPayCheckoutElement;
  }
}
